import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { IssuerValidators } from 'src/app/core/validators/issuer.validators';
import { IssuerEdit } from '../model/issuerEdit';

@Component({
  selector: 'app-issuer-edit',
  templateUrl: './issuer-edit.component.html',
  styleUrls: ['./issuer-edit.component.scss']
})
export class IssuerEditComponent
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<IssuerEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;

  public urlPattern = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  constructor(
    private fb: UntypedFormBuilder,
    private issuerValidators: IssuerValidators) { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get issuerUri(): UntypedFormControl
  {
    return this.editForm.get('issuerUri');
  }

  public get authorizeUri(): UntypedFormControl
  {
    return this.editForm.get('authorizeUri');
  }

  public get tokenUri(): UntypedFormControl
  {
    return this.editForm.get('tokenUri');
  }

  public get clientId(): UntypedFormControl
  {
    return this.editForm.get('clientId');
  }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get openIdUri(): UntypedFormControl
  {
    return this.editForm.get('openIdUri');
  }

  public get secret(): UntypedFormControl
  {
    return this.editForm.get('secret');
  }

  @Input() public set model(issuer: IssuerEdit)
  {
    this.initModel(issuer);
  }

  public isValidUri(uri: UntypedFormControl): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return uri.value !== undefined && uri.value && regEx.test(uri.value);
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(issuerEdit: IssuerEdit)
  {
    if (issuerEdit === undefined)
    {
      return;
    }

    // Set the current issuer id used for name async validators
    const previousIssuerName = this.isNew ? '' : issuerEdit.name;

    const urlValidatorsRequired = [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ];

    const urlValidators = [
      Validators.pattern(this.urlPattern)
    ];

    // Set form
    this.editForm = this.fb.group({
      id: [issuerEdit.id],
      version: [issuerEdit.version],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.issuerValidators.validateIssuerNameUnique(this.editForm.get('name'), previousIssuerName)
        }
      ],
      clientId: [
        { value: issuerEdit.clientId, disabled: false }, Validators.required
      ],
      description: { value: issuerEdit.description, disabled: false },
      issuerUri: [
        { value: issuerEdit.issuerUri, disabled: false },
        {
          validators: urlValidatorsRequired
        }
      ],
      authorizeUri: [
        { value: issuerEdit.authorizeUri, disabled: false },
        {
          validators: urlValidatorsRequired
        }
      ],
      tokenUri: [
        { value: issuerEdit.tokenUri, disabled: false },
        {
          validators: urlValidatorsRequired
        }
      ],
      openIdUri: [{ value: issuerEdit.openIdUri, disabled: false },
      {
        validators: urlValidators
      }],
      enabled: [{ value: issuerEdit.enabled, disabled: false }, Validators.required],
      secret: { value: issuerEdit.secret, disabled: false }
    });

    this.editForm.reset(issuerEdit);
    this.editFormActive = issuerEdit !== undefined;
  }
}
